// ── EVIDENCE P1.2 — the provisioning run record ────────────────────────────────────────────────
//
// ⚠ THIS PAGE IS THE REPORT. A run is queued work the tenant starts, leaves and comes back to, so the
//   durable record — not a toast — is where "Provisioned X · Skipped Y · Failed Z" lives, with every
//   skip and every failure named and its reason given in words.
//
// ⚠ THE TALLIES ARE THE SERVER'S, DERIVED FROM THE SAME ITEMS LISTED BELOW THEM. Nothing here counts
//   rows itself, so the headline cannot disagree with the list.
//
// ⚠ A WAIT IS NOT A FAILURE. When Microsoft asks VeriGRC to slow down, the run shows it is waiting and
//   when it resumes; the controls still to do are shown as waiting, never as failed.

import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { CARD_SURFACE } from '@/components/compliance/card-surface';
import { isFinishedRun, useProvisioningRun } from '@/api/hooks/useEvidenceProvisioning';
import type { ProvisioningOutcome, ProvisioningRunItem } from '@/api/types/evidence-provisioning.types';
import { REASON_LABEL, RUN_STATE_LABEL } from '@/components/compliance/provisioning-copy';

const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// ⚠ `pending` is titled at render: "Waiting" ONLY while the run is backing off for Microsoft, otherwise
//   "Remaining" — so "Waiting" always means throttled, never just "not done yet".
const SECTIONS: { outcome: ProvisioningOutcome; title: string; tone: string }[] = [
  { outcome: 'failed', title: 'Failed', tone: 'text-cert-expired-fg' },
  { outcome: 'skipped', title: 'Skipped', tone: 'text-cert-warn-fg' },
  { outcome: 'pending', title: 'Remaining', tone: 'text-text-secondary' },
  { outcome: 'provisioned', title: 'Provisioned', tone: 'text-cert-valid-fg' },
];

function ItemRow({ item }: { item: ProvisioningRunItem }) {
  return (
    <li className="border-b border-border-subtle px-[16px] py-[10px] last:border-b-0">
      <div className="flex items-baseline gap-[10px]">
        <Link to={`/compliance/workspace/controls/${item.control_reference}`}
          className="font-mono text-[12px] text-accent-cyan hover:underline">
          {item.display_code ?? item.control_reference}
        </Link>
        <span className="min-w-0 flex-1 truncate text-[13px] text-text-primary">{item.title ?? 'Unknown control'}</span>
        {item.reason_code && (
          <span className="text-[11px] font-medium text-text-secondary">{REASON_LABEL[item.reason_code]}</span>
        )}
      </div>
      {/* ⚠ The reason in words, as the server wrote it — not a code the user has to look up. */}
      {item.reason && <p className="mt-[3px] text-[12px] text-text-secondary">{item.reason}</p>}
    </li>
  );
}

export function ProvisioningRunPage() {
  const { runId } = useParams<{ runId: string }>();
  const { data, isLoading, isError } = useProvisioningRun(runId);

  if (isLoading) {
    return <div className="space-y-[12px] p-[24px]"><Skeleton className="h-[24px] w-[260px]" /><Skeleton className="h-[200px] w-full" /></div>;
  }
  if (isError || !data) {
    return <p className="p-[24px] text-[13px] text-text-secondary">This provisioning run could not be found.</p>;
  }

  const { run, items } = data;
  const c = run.counts;
  const finished = isFinishedRun(run.state);
  const waiting = !finished && run.resume_after;

  return (
    <div className="space-y-[12px] p-[15px]">
      <Link to="/compliance/workspace/controls" className="inline-flex items-center gap-[6px] text-[12px] text-text-secondary hover:text-text-primary">
        <ArrowLeft className="h-[12px] w-[12px]" aria-hidden="true" /> Back to controls
      </Link>

      <div className={cn(CARD_SURFACE, 'p-[16px]')}>
        <span className={EYEBROW}>Evidence location provisioning</span>
        <div className="mt-[6px] flex flex-wrap items-baseline gap-[12px]">
          <h1 className="text-[18px] font-semibold text-text-primary">
            {run.scope_kind === 'single' ? 'One control' : `${c.requested} controls`}
          </h1>
          <span className={cn('rounded-[6px] px-[8px] py-[2px] text-[11.5px] font-medium',
            run.state === 'completed' ? 'bg-cert-valid-bg text-cert-valid-fg'
              : run.state === 'completed_with_failures' ? 'bg-cert-warn-bg text-cert-warn-fg'
                : 'bg-bg-surface-2 text-text-secondary')}>
            {/* ⚠ A throttled run is `queued` in the database but WAITING to the person — one word, not two
                that disagree with the sentence under them. */}
            {waiting ? 'Waiting' : RUN_STATE_LABEL[run.state]}
          </span>
        </div>

        {/* ⚠ The headline tallies — every one a count of the rows listed below. */}
        <p className="mt-[10px] text-[14px] text-text-primary" data-testid="run-tallies">
          Provisioned {c.provisioned} · Skipped {c.skipped} · Failed {c.failed}
          {c.pending > 0 && <span className="text-text-secondary"> · {waiting ? 'Waiting' : 'Remaining'} {c.pending}</span>}
        </p>

        <p className="mt-[6px] text-[12px] text-text-faint">
          Started {fmtTime(run.triggered_at)}{finished && <> · Finished {fmtTime(run.finished_at)}</>}
        </p>
        {waiting && (
          <p className="mt-[8px] text-[12.5px] text-text-secondary">
            {/* ⓘ Box GA S3 (item 10) — neutral: a Box run defers on Box's throttle exactly as a SharePoint one does. */}
            The storage provider is temporarily limiting requests. Provisioning will resume automatically at {fmtTime(run.resume_after)}.
            No items have failed.
          </p>
        )}
        {!finished && !waiting && (
          <p className="mt-[8px] text-[12.5px] text-text-secondary">
            Provisioning continues in the background. You can leave this page and return later without losing progress.
          </p>
        )}
      </div>

      {SECTIONS.map(({ outcome, title: baseTitle, tone }) => {
        const rows = items.filter((i) => i.outcome === outcome);
        if (rows.length === 0) return null;
        const title = outcome === 'pending' && waiting ? 'Waiting' : baseTitle;
        return (
          <section key={outcome} className={CARD_SURFACE} aria-label={title}>
            <h2 className={cn('border-b border-border-subtle px-[16px] py-[9px] text-[12.5px] font-semibold', tone)}>
              {title} <span className="font-normal text-text-faint">{rows.length}</span>
            </h2>
            <ul>{rows.map((i) => <ItemRow key={i.control_reference} item={i} />)}</ul>
          </section>
        );
      })}

      <p className="px-[4px] text-[11.5px] text-text-faint">
        Provisioning creates a location for evidence; it does not mean evidence has been added. VeriGRC tracks these states separately.
      </p>
    </div>
  );
}
